import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import {
  Shield,
  Clock,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Loader2,
  RefreshCw,
  Filter,
  Key,
  BarChart3,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { Layout } from "../components/Layout";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle,
  DialogDescription 
} from "../components/ui/dialog";
import api from "../lib/api";
import { toast } from "sonner";

const STATUS_CONFIG_BASE = {
  pending: {
    cls: "bg-amber-100 text-amber-800 border-amber-200",
    icon: Clock,
  },
  admin_approved: {
    cls: "bg-blue-100 text-blue-800 border-blue-200",
    icon: Key,
  },
  completed: {
    cls: "bg-green-100 text-green-800 border-green-200",
    icon: CheckCircle2,
  },
  expired: {
    cls: "bg-red-100 text-red-800 border-red-200",
    icon: XCircle,
  },
};

const formatDate = (iso, tz) => {
  if (!iso) return "—";
  let s = String(iso).trim();
  if (!s.endsWith('Z') && !/[+-]\d{2}:\d{2}$/.test(s)) s += 'Z';
  return new Date(s).toLocaleString("ca-ES", {
    timeZone: tz || "Europe/Madrid",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const getTimeRemaining = (expiresAt, expiredLabel) => {
  if (!expiresAt) return null;
  const diff = new Date(expiresAt) - new Date();
  if (diff <= 0) return expiredLabel || "Expired";
  const h = Math.floor(diff / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
};

export default function EntityScrutinyPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [expandedId, setExpandedId] = useState(null);
  const [entityKey, setEntityKey] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [entityTz, setEntityTz] = useState("Europe/Madrid");

  useEffect(() => {
    loadRequests();
    api.get("/entities/me").then(res => {
      if (res.data?.timezone) setEntityTz(res.data.timezone);
    }).catch(() => {});
  }, []);

  const loadRequests = async () => {
    setLoading(true);
    try {
      const res = await api.get("/entity/scrutiny-requests");
      setRequests(res.data);
    } catch {
      toast.error(t('scrutinyRequests.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteScrutiny = async (token) => {
    if (!entityKey.trim()) {
      toast.error(t('scrutiny.enterPrivateKey'));
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post("/scrutiny/complete", {
        token,
        entity_key_part: entityKey.trim(),
      });
      toast.success(t('scrutiny.completedSuccess'));
      setEntityKey("");
      setExpandedId(null);
      // Navigate to results to auto-generate and send PDF
      const consultaId = res.data?.consulta_id;
      if (consultaId) {
        setTimeout(() => {
          navigate(`/consultes/${consultaId}/results?autoSendPdf=true`);
        }, 1000);
      } else {
        loadRequests();
      }
    } catch (error) {
      const detail = error.response?.data?.detail || t('errors.completingScrutiny');
      setErrorMsg(detail);
    } finally {
      setSubmitting(false);
    }
  };

  const filtered =
    filter === "all" ? requests : requests.filter((r) => r.status === filter);

  const counts = {
    all: requests.length,
    pending: requests.filter((r) => r.status === "pending").length,
    admin_approved: requests.filter((r) => r.status === "admin_approved")
      .length,
    completed: requests.filter((r) => r.status === "completed").length,
    expired: requests.filter((r) => r.status === "expired").length,
  };

  return (
    <Layout>
      <div className="max-w-4xl mx-auto p-4 md:p-8" data-testid="entity-scrutiny-page">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6"
        >
          <div>
            <h1 className="text-2xl sm:text-3xl font-heading font-bold text-slate-900">
              {t('entityScrutiny.title')}
            </h1>
            <p className="text-slate-500 mt-1 text-sm">
              {t('entityScrutiny.subtitle')}
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={loadRequests}
            disabled={loading}
            data-testid="refresh-entity-requests-btn"
          >
            <RefreshCw
              className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`}
            />
            {t('common.refresh')}
          </Button>
        </motion.div>

        {/* Filters */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="flex items-center gap-2 mb-6 flex-wrap"
          data-testid="entity-scrutiny-filters"
        >
          <Filter className="h-4 w-4 text-slate-400" />
          {[
            { key: "all", label: t('scrutiny.filterAll') },
            { key: "pending", label: t('scrutiny.filterPending') },
            { key: "admin_approved", label: t('scrutiny.filterPendingKey') },
            { key: "completed", label: t('scrutiny.filterCompleted') },
            { key: "expired", label: t('scrutiny.filterExpired') },
          ].map((f) => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors border ${
                filter === f.key
                  ? "bg-slate-900 text-white border-slate-900"
                  : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
              }`}
              data-testid={`entity-filter-${f.key}`}
            >
              {f.label} ({counts[f.key]})
            </button>
          ))}
        </motion.div>

        {/* Content */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
          </div>
        ) : filtered.length === 0 ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="bg-white border border-slate-200 rounded-xl p-12 text-center"
          >
            <AlertCircle className="h-12 w-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500">
              {t('entityScrutiny.noRequests')}
            </p>
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="space-y-3"
          >
            {filtered.map((req) => {
              const statusLabels = {
                pending: t('scrutiny.pendingAdmin'),
                admin_approved: t('scrutiny.pendingKey'),
                completed: t('scrutiny.completed'),
                expired: t('scrutiny.expired'),
              };
              const cfg = { ...(STATUS_CONFIG_BASE[req.status] || STATUS_CONFIG_BASE.pending), label: statusLabels[req.status] || req.status };
              const Icon = cfg.icon;
              const isExpanded = expandedId === req.id;
              const timeLeft =
                req.status === "pending" || req.status === "admin_approved"
                  ? getTimeRemaining(req.expires_at, t('scrutiny.expired'))
                  : null;

              return (
                <div
                  key={req.id}
                  className="bg-white border border-slate-200 rounded-xl overflow-hidden"
                  data-testid={`entity-scrutiny-request-${req.id}`}
                >
                  <div className="p-4 sm:p-5">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="font-semibold text-slate-900 truncate">
                            {req.votació_title}
                          </h3>
                          <span
                            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border shrink-0 ${cfg.cls}`}
                          >
                            <Icon className="h-3 w-3" />
                            {cfg.label}
                          </span>
                        </div>
                        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500">
                          <span>
                            {t('entityScrutiny.requested')}:{" "}
                            <span className="text-slate-700">
                              {formatDate(req.created_at, entityTz)}
                            </span>
                          </span>
                          {req.admin_approved_at && (
                            <span>
                              {t('entityScrutiny.approved')}:{" "}
                              <span className="text-slate-700">
                                {formatDate(req.admin_approved_at, entityTz)}
                              </span>
                            </span>
                          )}
                          {req.completed_at && (
                            <span>
                              {t('entityScrutiny.completedAt')}:{" "}
                              <span className="text-slate-700">
                                {formatDate(req.completed_at, entityTz)}
                              </span>
                            </span>
                          )}
                          {timeLeft && (
                            <span>
                              {t('entityScrutiny.expires')}:{" "}
                              <span
                                className={
                                  timeLeft === t('scrutiny.expired')
                                    ? "text-red-600"
                                    : "text-amber-600"
                                }
                              >
                                {timeLeft}
                              </span>
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {req.status === "admin_approved" && (
                          <Button
                            size="sm"
                            onClick={() => {
                              setExpandedId(isExpanded ? null : req.id);
                              setEntityKey("");
                            }}
                            className="bg-blue-600 hover:bg-blue-700 gap-1.5"
                            data-testid={`enter-key-btn-${req.id}`}
                          >
                            <Key className="h-4 w-4" />
                            {t('entityScrutiny.enterKey')}
                            {isExpanded ? (
                              <ChevronUp className="h-3 w-3" />
                            ) : (
                              <ChevronDown className="h-3 w-3" />
                            )}
                          </Button>
                        )}
                        {req.status === "completed" && (
                          <Link to={`/consultes/${req.votació_id}/results`}>
                            <Button
                              size="sm"
                              variant="outline"
                              className="gap-1.5"
                              data-testid={`view-results-btn-${req.id}`}
                            >
                              <BarChart3 className="h-4 w-4" />
                              {t('scrutiny.viewResults')}
                            </Button>
                          </Link>
                        )}
                        {req.status === "pending" && (
                          <span className="text-xs text-amber-600">
                            {t('entityScrutiny.waitingAdmin')}
                          </span>
                        )}
                        {req.status === "expired" && (
                          <span className="text-xs text-red-600">{t('admin.expired')}</span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Expanded key input */}
                  {isExpanded && req.status === "admin_approved" && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      className="border-t border-slate-200 bg-blue-50 p-4 sm:p-5"
                    >
                      <div className="flex items-start gap-3 mb-3">
                        <Shield className="h-5 w-5 text-blue-600 mt-0.5" />
                        <p className="text-sm text-blue-800">
                          {t('entityScrutiny.adminApprovedDesc')}
                        </p>
                      </div>
                      <Textarea
                        value={entityKey}
                        onChange={(e) => setEntityKey(e.target.value)}
                        placeholder={t('entityScrutiny.keyPlaceholder')}
                        className="h-28 font-mono text-xs mb-3 bg-white"
                        data-testid={`entity-key-input-${req.id}`}
                      />
                      <Button
                        onClick={() => handleCompleteScrutiny(req.token)}
                        disabled={submitting || !entityKey.trim()}
                        className="w-full sm:w-auto bg-green-600 hover:bg-green-700"
                        data-testid={`complete-scrutiny-btn-${req.id}`}
                      >
                        {submitting ? (
                          <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        ) : (
                          <CheckCircle2 className="h-4 w-4 mr-2" />
                        )}
                        {t('entityScrutiny.completeScrutiny')}
                      </Button>
                    </motion.div>
                  )}
                </div>
              );
            })}
          </motion.div>
        )}
      </div>

      {/* Error Dialog */}
      <Dialog open={!!errorMsg} onOpenChange={() => {}}>
        <DialogContent className="max-w-md" onPointerDownOutside={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <XCircle className="h-5 w-5" />
              {t('entityScrutiny.incorrectKey')}
            </DialogTitle>
            <DialogDescription className="text-slate-600 pt-2">
              {errorMsg}
            </DialogDescription>
          </DialogHeader>
          <p className="text-sm text-slate-500">
            {t('entityScrutiny.reviewKey')}
          </p>
          <div className="flex justify-end pt-2">
            <Button
              onClick={() => { setErrorMsg(""); setEntityKey(""); }}
              className="bg-slate-900 hover:bg-slate-800"
              data-testid="error-accept-btn"
            >
              {t('common.accept')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
